/**
 * Notifications agence -> Easers (GRH > Notifications).
 * - display_mode 'popup' : pop-up à chaque démarrage de l'application pendant la durée d'affichage.
 * - display_mode 'banner' : bandeau en haut de l'espace pendant la durée d'affichage (refermable jusqu'au prochain démarrage).
 * - display_mode 'both'  : pop-up jusqu'à la première lecture, puis bandeau.
 *
 * « Démarrage » : sur une application installée, sessionStorage ne se réinitialise presque jamais (l'app reste en
 * tâche de fond des jours entiers), donc on détecte nous-mêmes un démarrage : chargement de page après plus de
 * 30 minutes sans activité, ou retour au premier plan après plus de 30 minutes. L'état « déjà vu / fermé » est rangé
 * dans localStorage avec l'identifiant du démarrage courant.
 */
import { getToken } from './easerApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const START_KEY = 'pts_easer_start_id';
const LAST_ACTIVE_KEY = 'pts_easer_last_active';
const NEW_START_AFTER_MS = 30 * 60 * 1000;
const EVENT = 'easer:app-start';

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode / storage full */ } },
};

let startId = null;

const touch = () => ls.set(LAST_ACTIVE_KEY, String(Date.now()));

/** Begin a new "app start" (popups shown again, closed banners back). */
const beginStart = () => {
  startId = String(Date.now());
  ls.set(START_KEY, startId);
  touch();
  try { window.dispatchEvent(new Event(EVENT)); } catch { /* old browsers */ }
};

/** Current start id; a new one is created when the app was inactive for more than 30 minutes. */
export const currentStartId = () => {
  if (startId) return startId;
  const last = Number(ls.get(LAST_ACTIVE_KEY) || 0);
  const stored = ls.get(START_KEY);
  if (stored && Date.now() - last < NEW_START_AFTER_MS) {
    startId = stored; // page reload within an active session: same start
    touch();
  } else {
    beginStart();
  }
  return startId;
};

if (typeof window !== 'undefined') {
  currentStartId();
  setInterval(() => { if (document.visibilityState !== 'hidden') touch(); }, 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { touch(); return; }
    const last = Number(ls.get(LAST_ACTIVE_KEY) || 0);
    if (Date.now() - last >= NEW_START_AFTER_MS) beginStart(); else touch();
  });
}

/** Subscribe to "new app start" events (the page re-evaluates pop-ups / banners). Returns an unsubscribe fn. */
export const onAppStart = (handler) => {
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
};

export const notificationImageUrl = (id) => `${BACKEND_URL}/api/easer/notifications/${id}/image?token=${encodeURIComponent(getToken())}`;

const popupKey = (id) => `easer_notif_popup_${id}`;
const bannerKey = (id) => `easer_notif_banner_${id}`;
const seenThisStart = (k) => ls.get(k) === currentStartId();

/** Notifications to show as a pop-up at this start of the app, oldest first (one after the other). */
export const popupCandidates = (items) => items
  .filter((n) => n.display_active && (n.display_mode === 'popup' || (n.display_mode === 'both' && !n.read_at)) && !seenThisStart(popupKey(n.id)))
  .slice()
  .reverse();

export const markPopupShown = (id) => ls.set(popupKey(id), currentStartId());

/** Notifications to show as a top banner (not closed since this start of the app). */
export const bannerCandidates = (items) => items
  .filter((n) => n.display_active && (n.display_mode === 'banner' || (n.display_mode === 'both' && n.read_at)) && !seenThisStart(bannerKey(n.id)));

export const dismissBanner = (id) => ls.set(bannerKey(id), currentStartId());

export const KIND_LABELS = { challenge: 'Challenge', message: 'Message' };

/** Let the page refresh the notifications (badge, banners) after a read. */
export const notifyNotificationsChanged = () => { try { window.dispatchEvent(new Event('easer:notifications-changed')); } catch { /* old browsers */ } };
